import type { TerreiroOnboardingFormState } from "gsintegrations";
import { OnboardingFormChatPanel, OnboardingTerreiroPage } from "gsintegrations";
import type { ComponentProps } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { trackOnboardingProductEvent } from "../analytics/onboarding";
import { resolvePageKeyFromPathname } from "../analytics/pageRegistry";
import {
  trackSignupEvent,
  type SignupAnalyticsContext,
} from "../analytics/signup";
import { ROUTE } from "../constants/routes";
import { useCachedAuthProfile } from "../hooks/useCachedAuthProfile";
import { useBankAccountsQuery } from "../hooks/useBankAccountsQuery";
import {
  resolveSignupAttributionCode,
  sanitizeInternalReturnTo,
} from "../utils/authQueryParams";
import useQuickTerreiroSignupMutation from "../hooks/useQuickTerreiroSignupMutation";
import useQuickTerreiroSocialSignupMutation from "../hooks/useQuickTerreiroSocialSignupMutation";
import { useReligionRanksQuery } from "../hooks/useReligionRanksQuery";
import { useReligionsQuery } from "../hooks/useReligionsQuery";
import { useRolesQuery } from "../hooks/useRolesQuery";
import { useSignupAnalyticsContext } from "../hooks/useSignupAnalyticsContext";
import {
  buildProductBaseFromRegistry,
  useProductAnalyticsContext,
} from "../hooks/useProductAnalyticsContext";
import { useOnboardingFormChat } from "../hooks/useOnboardingFormChat";
import type { OnboardingChatFormState } from "../repositories/onboardingFormChat.repository";
import { terreiroSignupRepository } from "../repositories/terreiroSignupRepository";
import {
  sanitizeQuickTerreiroSignupPayload,
  sanitizeQuickTerreiroSocialSignupPayload,
} from "../sanitizers";
import {
  ONBOARDING_CHAT_BOT_SUBTITLE,
  ONBOARDING_CHAT_BOT_TITLE,
} from "../constants/onboardingChatBot.metadata";
import {
  buildTerreiroSignupFromChat,
  buildTerreiroSocialSignupFromChat,
} from "../utils/onboardingFormChatPayloadBuilder";
import type {
  QuickTerreiroSignupPayload,
  QuickTerreiroSocialSignupPayload,
  TerreiroSignupPayload,
} from "../types/terreiroSignup.types";

export default function OnboardingTerreiro() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = sanitizeInternalReturnTo(
    searchParams.get("returnTo"),
    ROUTE.APP_HOME,
  );
  const fallbackSignupAnalyticsContext = useSignupAnalyticsContext();
  const productAnalyticsContext = useProductAnalyticsContext();
  const authProfile = useCachedAuthProfile();
  const {
    firstName,
    lastName,
    email,
    cellphone,
    socialAuth,
    authenticatedTerreiroCreation,
    code,
    signupAnalyticsContext: signupAnalyticsStateContext,
  } = (location.state || {}) as {
    firstName?: string;
    lastName?: string;
    email?: string;
    cellphone?: string;
    socialAuth?: boolean;
    authenticatedTerreiroCreation?: boolean;
    code?: string;
    signupAnalyticsContext?: SignupAnalyticsContext;
  };
  const resolvedSignupCode = resolveSignupAttributionCode(
    searchParams,
    code,
  );

  const signupAnalyticsContext = useMemo(
    () => ({
      ...fallbackSignupAnalyticsContext,
      ...(signupAnalyticsStateContext ?? {}),
      page_url: fallbackSignupAnalyticsContext.page_url,
      context_timestamp: fallbackSignupAnalyticsContext.context_timestamp,
    }),
    [fallbackSignupAnalyticsContext, signupAnalyticsStateContext],
  );

  const productBase = useMemo(() => {
    const registryEntry = resolvePageKeyFromPathname(location.pathname);
    return registryEntry
      ? buildProductBaseFromRegistry(
          registryEntry,
          productAnalyticsContext,
          location,
        )
      : {
          pageKey: "onboarding_terreiro",
          routePath: location.pathname,
          sourcePage: "OnboardingTerreiro",
          productArea: "onboarding" as const,
          activeProfileType: productAnalyticsContext.activeProfileType,
          terreiroId: productAnalyticsContext.terreiroId,
          mediumId: productAnalyticsContext.mediumId,
        };
  }, [location, productAnalyticsContext]);

  // Get onboarding mode from URL param, default to "quick" for faster signup
  const modeParam = searchParams.get("mode");
  const mode: "quick" | "complete" =
    modeParam === "complete" ? "complete" : "quick";

  // Build memoized initial data with a typed helper (single responsibility).
  const initialData = useMemo<Partial<TerreiroOnboardingFormState>>(() => {
    return {
      firstName: firstName ?? "",
      lastName: lastName ?? "",
      phone: cellphone ?? "",
      terreiroName: "",
      foundationDate: "",
      initiationDate: "",
      religionId: "",
      isMedium: true,
      rankId: "",
      leader: true,
      roleId: "",
      account: "",
      agency: "",
      bankName: "",
      initialBalance: "",
      dueDay: "7",
      hasBankAccount: true,
      monthlyFee: "",
      paymentMethod: "",
      pixKey: "",
      pixKeyType: "",
      receivingAccount: "",
      email: email ?? "",
    };
  }, [firstName, lastName, cellphone, email]);

  // Persist only a boolean 'signupCompleted' instead of storing whole response.
  // This avoids saving potentially sensitive or uncontrolled API payloads in localStorage.
  const [signupCompleted, setSignupCompleted] = useState<boolean>(() => {
    try {
      return localStorage.getItem("signupCompleted") === "true";
    } catch {
      return false;
    }
  });

  // Error state for signup
  const [signupError, setSignupError] = useState<
    { code?: string; message: string } | undefined
  >(undefined);
  const [hasSignupSuccess, setHasSignupSuccess] = useState(false);
  const [didRedirectHome, setDidRedirectHome] = useState(false);

  // Keep minimal signupCompleted state for persistence. Use mutation state
  // (isLoading / isError / error) for submit UI and error handling.

  // Static lists via React Query hooks (consistent with repository pattern)
  // In quick mode, we don't need roles and bankOptions (they require auth)
  const isQuickMode = mode === "quick";
  const { data: religions = [] } = useReligionsQuery();
  const { data: roles = [] } = useRolesQuery({
    terreiroId: "",
    enabled: !isQuickMode,
  });
  const { data: bankOptions = [] } = useBankAccountsQuery({
    enabled: !isQuickMode,
  });

  // Sync minimal flag to localStorage only.
  useEffect(() => {
    try {
      if (signupCompleted) {
        localStorage.setItem("signupCompleted", "true");
      } else {
        localStorage.removeItem("signupCompleted");
      }
    } catch {
      // Fail silently for localStorage errors (e.g., private mode)
    }
  }, [signupCompleted]);

  // Selected religion controls the ranks query; keep as nullable to express absence.
  const [selectedReligionId, setSelectedReligionId] = useState<string | null>(
    null,
  );

  // If the ranks hook supports enabled: boolean inside the hook, prefer that.
  const { data: ranks = [] } = useReligionRanksQuery(selectedReligionId ?? "");

  // Stable handler for religion selection.
  const handleReligionSelect = useCallback((religionId: string) => {
    setSelectedReligionId(religionId);
  }, []);

  const navigateHome = useCallback(() => {
    setDidRedirectHome(true);
    trackOnboardingProductEvent("onboarding_completed", productBase, {
      role: "terreiro",
      onboarding_mode: mode,
      trigger: "mutation",
    });
    trackSignupEvent("signup_redirect_to_home", signupAnalyticsContext, {
      role: "terreiro",
      onboarding_mode: mode,
    });
    navigate(returnTo, { replace: true });
  }, [mode, navigate, productBase, returnTo, signupAnalyticsContext]);

  useEffect(
    () => () => {
      if (hasSignupSuccess && !didRedirectHome) {
        trackSignupEvent(
          "signup_abandon_after_success",
          signupAnalyticsContext,
          {
            role: "terreiro",
            onboarding_mode: mode,
          },
        );
      }
    },
    [hasSignupSuccess, didRedirectHome, signupAnalyticsContext, mode],
  );

  // Complete mode uses /terreiro-signup (live endpoint)
  const [isCompleteSubmitting, setIsCompleteSubmitting] = useState(false);

  const { mutateAsync: quickSignup } = useQuickTerreiroSignupMutation();
  const { mutateAsync: quickSocialSignup } =
    useQuickTerreiroSocialSignupMutation();

  const isLoading = isCompleteSubmitting;

  const handleComplete = useCallback(
    async (
      formData: Partial<TerreiroOnboardingFormState>,
    ): Promise<boolean> => {
      setSignupError(undefined);

      trackSignupEvent(
        "signup_onboarding_submit_attempt",
        signupAnalyticsContext,
        {
          role: "terreiro",
          onboarding_mode: mode,
        },
      );

      try {
        if (mode === "quick") {
          const chatState = formData as OnboardingChatFormState;
          const shouldUseSocialSignup =
            Boolean(socialAuth || authenticatedTerreiroCreation) ||
            (!chatState.password && !chatState.confirmPassword);
          if (shouldUseSocialSignup) {
            const socialPayload: QuickTerreiroSocialSignupPayload = {
              ...buildTerreiroSocialSignupFromChat(chatState),
              ...(resolvedSignupCode && { signupCode: resolvedSignupCode }),
            };
            await quickSocialSignup(
              sanitizeQuickTerreiroSocialSignupPayload(socialPayload),
            );
          } else {
            const quickPayload: QuickTerreiroSignupPayload = {
              ...buildTerreiroSignupFromChat(chatState),
              ...(resolvedSignupCode && { signupCode: resolvedSignupCode }),
            };
            await quickSignup(sanitizeQuickTerreiroSignupPayload(quickPayload));
          }
          setSignupCompleted(true);
          setHasSignupSuccess(true);
          trackSignupEvent("signup_success", signupAnalyticsContext, {
            role: "terreiro",
            onboarding_mode: mode,
          });
          navigateHome();
          return true;
        } else {
          const merged: Record<string, unknown> = {
            ...initialData,
            ...formData,
            ...(resolvedSignupCode && {
              signupCode: resolvedSignupCode,
            }),
          };

          const sanitized: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(merged)) {
            if (v === "") continue;
            sanitized[k] = v;
          }

          setIsCompleteSubmitting(true);
          try {
            await terreiroSignupRepository.signup(
              sanitized as unknown as TerreiroSignupPayload,
            );
            setSignupCompleted(true);
            setHasSignupSuccess(true);
            trackSignupEvent("signup_success", signupAnalyticsContext, {
              role: "terreiro",
              onboarding_mode: mode,
            });
            navigateHome();
            return true;
          } finally {
            setIsCompleteSubmitting(false);
          }
        }
      } catch (error) {
        const message =
          error && typeof error === "object" && "message" in error
            ? ((error as { message?: string }).message ?? String(error))
            : String(error);

        trackSignupEvent("signup_error", signupAnalyticsContext, {
          role: "terreiro",
          onboarding_mode: mode,
          error_message: message,
        });

        if (error && typeof error === "object" && "message" in error) {
          const normalizedError = error as { code?: string; message?: string };
          setSignupError({
            code: normalizedError.code,
            message: normalizedError.message ?? String(error),
          });
        } else {
          setSignupError({ message: String(error) });
        }
        return false;
      }
    },
    [
      initialData,
      quickSignup,
      quickSocialSignup,
      socialAuth,
      authenticatedTerreiroCreation,
      resolvedSignupCode,
      mode,
      navigateHome,
      signupAnalyticsContext,
    ],
  );

  const handleFinishAutoRedirect = useCallback(() => {
    // Read the minimal flag rather than arbitrary response payload.
    const stored = localStorage.getItem("signupCompleted");
    if (stored === "true") {
      navigateHome();
    } else {
      // keep previous behavior but avoid verbose console usage
    }
  }, [navigateHome]);

  const referenceData = useMemo(
    () => ({
      religions: religions.map((religion) => ({
        id: religion.id,
        name: religion.name,
      })),
      ranks:
        mode === "complete"
          ? ranks.map((rank) => ({ id: rank.id, name: rank.name }))
          : undefined,
      roles:
        mode === "complete"
          ? roles.map((role) => ({ id: role.id, name: role.name }))
          : undefined,
      bankOptions:
        mode === "complete"
          ? bankOptions.map((bank) => bank.name)
          : undefined,
    }),
    [bankOptions, mode, ranks, religions, roles],
  );

  const prefilledData = useMemo(
    () => ({
      firstName: initialData.firstName,
      lastName: initialData.lastName,
      email: initialData.email,
      phone: initialData.phone,
    }),
    [initialData],
  );

  const handleCompleteRef = useRef(handleComplete);
  handleCompleteRef.current = handleComplete;

  const handleChatConfirmed = useCallback(
    async (formState: OnboardingChatFormState) => {
      return handleCompleteRef.current(
        formState as Partial<TerreiroOnboardingFormState>,
      );
    },
    [],
  );

  const formChat = useOnboardingFormChat({
    entityType: "terreiro_onboarding",
    mode,
    isSocialAuth: Boolean(socialAuth || authenticatedTerreiroCreation),
    referenceData,
    prefilledData,
    signupAnalyticsContext,
    onConfirmed: handleChatConfirmed,
  });

  const handleOnCancel = useCallback(() => {
    navigate(socialAuth ? ROUTE.SIGNUP_TYPE : "/signup");
  }, [navigate, socialAuth]);

  const pageProps = {
    mode,
    isSocialAuth: Boolean(socialAuth),
    initialData,
    religions,
    // Quick mode doesn't need roles, ranks, and bankOptions
    roles: mode === "quick" ? [] : roles,
    ranks: mode === "quick" ? [] : ranks,
    bankOptions: mode === "quick" ? [] : bankOptions,
    onReligionSelect: handleReligionSelect,
    onCancel: handleOnCancel,
    onComplete: handleComplete,
    onFinishAutoRedirect: handleFinishAutoRedirect,
    // Extra UI props we want to pass when supported by the wrapped component
    isSubmitting: isLoading,
    error: signupError
      ? {
          message: signupError.message,
        }
      : undefined,
  } as unknown as ComponentProps<typeof OnboardingTerreiroPage>;

  if (formChat.isFeatureEnabled) {
    return (
      <OnboardingFormChatPanel
        entityType="terreiro_onboarding"
        title={ONBOARDING_CHAT_BOT_TITLE}
        subtitle={ONBOARDING_CHAT_BOT_SUBTITLE}
        messages={formChat.messages}
        quickReplies={formChat.quickReplies}
        summary={formChat.summary}
        currentStep={formChat.currentStep}
        stepLabels={formChat.stepLabels}
        stepOrder={formChat.stepOrder}
        isLoading={formChat.isLoading || isLoading}
        error={
          signupError
            ? {
                code: signupError.code ?? "SIGNUP_ERROR",
                message: signupError.message,
              }
            : formChat.error
        }
        confirmLabel="Criar conta"
        voiceEnabled={false}
        activeField={formChat.activeField}
        fieldInput={formChat.fieldInput}
        fieldOptions={formChat.fieldOptions}
        selectedOption={formChat.selectedOption}
        userAvatarUrl={authProfile?.avatarUrl}
        onTranscribeAudio={undefined}
        onSendMessage={formChat.handleSendMessage}
        onSelectOption={formChat.handleSelectOption}
        onConfirm={formChat.handleConfirm}
        onEdit={formChat.handleEdit}
        onCancelEdit={formChat.handleCancelEdit}
        onEditField={formChat.handleEditField}
        editInteraction={formChat.editInteraction}
        editingFieldLabel={formChat.editingFieldLabel}
        editableFieldOptions={formChat.editableFieldOptions}
        onReset={formChat.resetConversation}
        onClose={handleOnCancel}
      />
    );
  }

  return <OnboardingTerreiroPage {...pageProps} />;
}
